import type { JsonPatch, Spec, UIElement } from "@json-render/core";
import { setByPath } from "@json-render/core";
import type { UIMessageStreamWriter } from "ai";
import { VIZ_STREAM_EVENT } from "@/lib/ai/visualization/config";
import type { VizDataset } from "@/lib/ai/visualization/data/schema";
import type {
  VizDatasetEnvelope,
  VizRenderOutput,
  VizStreamPatchPayload,
} from "@/lib/ai/visualization/types";

/**
 * `viz_stream` patch protocol の送受信ヘルパー群。
 *
 * @remarks
 * 本ファイルは server 側の patch emit と client 側の patch replay の両方を持つ。
 * 送受信で protocol 定義が drift しやすいため、path 制約・配信順・復元規則を
 * 1 ファイルへ寄せて SSOT にする。
 *
 * client 側では「受信 patch を安全適用する責務」と
 * 「render output と dataset index から描画用 envelope を再構成する責務」を持つ。
 * view ごとの projection は server compile の時点で確定しているため、client は
 * persisted `datasetForRender` を最優先で読み、source snapshot への再投影は行わない。
 */

/**
 * `viz_stream` patch イベントを送信する。
 */
export function emitPatch(
  writer: UIMessageStreamWriter,
  payload: VizStreamPatchPayload,
): void {
  writer.write({
    type: VIZ_STREAM_EVENT,
    transient: true,
    data: payload,
  });
}

/**
 * Spec を段階描画用 patch 配列へ変換する。
 */
export function buildStreamingPatches(tree: Spec): JsonPatch[] {
  const rootKey = tree.root;
  if (!rootKey) return [];
  const root = tree.elements[rootKey];
  if (!root) return [];

  const patches: JsonPatch[] = [
    { op: "replace", path: "/root", value: rootKey },
  ];
  const visited = new Set<string>();

  const emitElement = (elementKey: string) => {
    if (visited.has(elementKey)) return;
    visited.add(elementKey);
    const element = tree.elements[elementKey];
    if (!element) return;

    const children = Array.isArray(element.children) ? element.children : [];
    const streamValue: UIElement =
      children.length > 0
        ? {
            ...element,
            // 子要素を先行して配信し、途中状態で未定義参照が起きるのを防ぐ。
            children: [],
          }
        : element;

    patches.push({
      op: "replace",
      path: `/elements/${elementKey}`,
      value: streamValue satisfies UIElement,
    });

    let childIndex = 0;
    for (const childKey of children) {
      emitElement(childKey);
      patches.push({
        op: "add",
        path: `/elements/${elementKey}/children/${childIndex}`,
        value: childKey,
      });
      childIndex += 1;
    }
  };

  emitElement(rootKey);

  return patches;
}

/**
 * streaming patch 適用前の初期ツリー。
 */
export const EMPTY_VIZ_TREE: Spec = {
  root: "",
  elements: {},
};

const UNSAFE_PATH_SEGMENTS = new Set(["__proto__", "prototype", "constructor"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isUnsafeSegment(segment: string): boolean {
  return UNSAFE_PATH_SEGMENTS.has(segment);
}

function hasUnsafeSegments(segments: string[]): boolean {
  return segments.some((segment) => isUnsafeSegment(segment));
}

function isAllowedPatchPath(path: string): boolean {
  if (path === "/root") return true;
  if (!path.startsWith("/elements/")) return false;

  const remainder = path.slice("/elements/".length);
  const segments = remainder.split("/").filter((segment) => segment.length > 0);
  if (segments.length === 0) return false;
  if (hasUnsafeSegments(segments)) return false;

  const [elementKey, section] = segments;
  if (!elementKey) return false;
  if (isUnsafeSegment(elementKey)) return false;
  if (!section) return true;

  if (section === "props") return true;
  if (section === "children") return true;

  return false;
}

function isValidPatch(patch: JsonPatch): boolean {
  if (!patch || typeof patch !== "object") return false;
  const op = patch.op;
  if (op !== "add" && op !== "remove" && op !== "replace") {
    return false;
  }
  if (typeof patch.path !== "string") return false;
  if (!isAllowedPatchPath(patch.path)) return false;
  return true;
}

/**
 * 1行JSON patch を安全制約付きで解析する。
 *
 * 許可パス外・不正 op は null を返し、呼び出し側は fail-fast で無視できる。
 */
export function parseVizPatchLine(line: string): JsonPatch | null {
  try {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) {
      return null;
    }
    const parsed = JSON.parse(trimmed) as JsonPatch;
    if (!isValidPatch(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * 単一 patch をツリーへ適用し、children 重複/不整合を正規化する。
 */
export function applyVizPatch(tree: Spec, patch: JsonPatch): Spec {
  if (!isValidPatch(patch)) {
    return tree;
  }
  const nextTree: Spec = {
    ...tree,
    elements: { ...tree.elements },
  };

  switch (patch.op) {
    case "add":
    case "replace": {
      if (patch.path === "/root") {
        if (typeof patch.value === "string") {
          nextTree.root = patch.value;
        }
        break;
      }

      if (patch.path.startsWith("/elements/")) {
        const pathParts = patch.path.slice("/elements/".length).split("/");
        const elementKey = pathParts[0];
        if (!elementKey) return nextTree;

        if (pathParts.length === 1) {
          const normalized = normalizeElement(patch.value);
          if (normalized) {
            nextTree.elements[elementKey] = normalized;
          }
        } else {
          const element = nextTree.elements[elementKey];
          if (element) {
            const propPath = "/" + pathParts.slice(1).join("/");
            const newElement = { ...element };
            setByPath(
              newElement as unknown as Record<string, unknown>,
              propPath,
              patch.value,
            );
            nextTree.elements[elementKey] = newElement;
          }
        }
      }
      break;
    }
    case "remove": {
      if (patch.path.startsWith("/elements/")) {
        const elementKey = patch.path.slice("/elements/".length).split("/")[0];
        if (!elementKey) return nextTree;

        const pathParts = patch.path.slice("/elements/".length).split("/");
        const section = pathParts[1];
        const subPath = pathParts.slice(2);

        if (!section) {
          const { [elementKey]: _, ...rest } = nextTree.elements;
          nextTree.elements = rest;
          break;
        }

        const element = nextTree.elements[elementKey];
        if (!element) return nextTree;

        if (section === "props") {
          nextTree.elements[elementKey] = removeElementProps(element, subPath);
          break;
        }

        if (section === "children") {
          nextTree.elements[elementKey] = removeElementChildren(
            element,
            subPath,
          );
          break;
        }
      }
      break;
    }
    default:
      break;
  }

  return normalizeVizTreeChildren(nextTree);
}

function normalizeElement(value: unknown): UIElement | null {
  if (!isRecord(value)) return null;
  const element = value as Record<string, unknown>;
  const type =
    typeof element.type === "string" && element.type.length > 0
      ? element.type
      : null;
  if (!type) return null;
  const props = sanitizeValue(isRecord(element.props) ? element.props : {});
  const children = sanitizeChildren(element.children) ?? [];
  return { type, props, children };
}

function sanitizeChildren(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const next = value.filter(
    (child): child is string => typeof child === "string",
  );
  if (next.length === 0) return [];
  const seen = new Set<string>();
  const unique: string[] = [];
  for (let index = next.length - 1; index >= 0; index -= 1) {
    const child = next[index];
    if (!child) continue;
    if (seen.has(child)) continue;
    seen.add(child);
    unique.push(child);
  }
  unique.reverse();
  return unique;
}

function sanitizeValue(value: unknown): Record<string, unknown> {
  if (!isRecord(value)) return {};
  return sanitizeRecord(value);
}

function sanitizeRecord(
  value: Record<string, unknown>,
): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (isUnsafeSegment(key)) continue;
    next[key] = sanitizeNestedValue(raw);
  }
  return next;
}

function sanitizeNestedValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeNestedValue(item));
  }
  if (isRecord(value)) {
    return sanitizeRecord(value);
  }
  return value;
}

function removeElementProps(element: UIElement, path: string[]): UIElement {
  const nextProps =
    element.props && typeof element.props === "object"
      ? { ...(element.props as Record<string, unknown>) }
      : {};

  if (path.length === 0) {
    return { ...element, props: {} };
  }

  removeByPath(nextProps, path);
  return { ...element, props: nextProps };
}

function removeElementChildren(element: UIElement, path: string[]): UIElement {
  const nextChildren = Array.isArray(element.children)
    ? [...element.children]
    : [];

  if (path.length === 0) {
    return { ...element, children: [] };
  }

  const [target, ...rest] = path;
  if (!target) return element;

  if (rest.length > 0) {
    return element;
  }

  const index = Number(target);
  if (!Number.isNaN(index) && Number.isInteger(index)) {
    if (index >= 0 && index < nextChildren.length) {
      nextChildren.splice(index, 1);
    }
    return { ...element, children: nextChildren };
  }

  const filtered = nextChildren.filter((child) => child !== target);
  return { ...element, children: filtered };
}

function removeByPath(
  target: Record<string, unknown> | unknown[],
  path: string[],
) {
  if (path.length === 0) return;
  const [head, ...rest] = path;
  if (!head) return;

  if (Array.isArray(target)) {
    const index = Number(head);
    if (Number.isNaN(index) || !Number.isInteger(index)) return;
    if (rest.length === 0) {
      if (index >= 0 && index < target.length) {
        target.splice(index, 1);
      }
      return;
    }
    const next = target[index];
    if (next && typeof next === "object") {
      const cloned = Array.isArray(next) ? [...next] : { ...(next as object) };
      removeByPath(cloned as Record<string, unknown> | unknown[], rest);
      target[index] = cloned;
    }
    return;
  }

  if (rest.length === 0) {
    delete target[head];
    return;
  }

  const next = target[head];
  if (!next || typeof next !== "object") return;
  const cloned = Array.isArray(next) ? [...next] : { ...(next as object) };
  removeByPath(cloned as Record<string, unknown> | unknown[], rest);
  target[head] = cloned;
}

/**
 * ツリー内 children の重複・順序ゆらぎを除去して正規形へそろえる。
 */
export function normalizeVizTreeChildren(tree: Spec): Spec {
  let changed = false;
  const nextElements: Record<string, UIElement> = {};
  for (const [key, element] of Object.entries(tree.elements) as Array<
    [string, UIElement]
  >) {
    let nextElement = element;
    if (!Array.isArray(nextElement.children)) {
      nextElements[key] = nextElement;
      continue;
    }
    const unique = sanitizeChildren(nextElement.children) ?? [];
    const sameLength = unique.length === nextElement.children.length;
    const sameOrder = sameLength
      ? unique.every((child, index) => child === nextElement.children?.[index])
      : false;
    if (!sameOrder) {
      changed = true;
      nextElement = { ...nextElement, children: unique };
    }
    nextElements[key] = nextElement;
  }
  if (!changed) return tree;
  return { ...tree, elements: nextElements };
}

/**
 * `toolCallId -> datasetId -> dataset` で保持する参照インデックス。
 */
export type VizDatasetIndex = Map<string, Map<string, VizDataset>>;

function isRenderableDataset(value: unknown): value is VizDataset {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    Array.isArray(value.fields) &&
    Array.isArray(value.rows)
  );
}

function findDataset(args: {
  datasetIndex: VizDatasetIndex;
  datasetId: string;
  toolCallId?: string;
}): VizDataset | null {
  const scoped = args.toolCallId
    ? args.datasetIndex.get(args.toolCallId)
    : null;
  const direct = scoped?.get(args.datasetId);
  if (direct) return direct;

  for (const datasets of args.datasetIndex.values()) {
    const dataset = datasets.get(args.datasetId);
    if (dataset) return dataset;
  }
  return null;
}

/**
 * render 結果で参照された datasetId を `viz.datasets` 形式へ再構成する。
 *
 * @remarks
 * compile 時に確定した `datasetForRender` があればそれを最優先し、
 * persisted output が古い shape のときだけ source snapshot 由来の dataset index を使う。
 * 指定 toolCallId のスコープを優先し、見つからない場合のみ全スコープを探索する。
 */
export function buildDatasetEnvelopeForRender(
  output: Pick<VizRenderOutput, "datasetId" | "datasetForRender"> | null,
  datasetIndex: VizDatasetIndex,
  toolCallId?: string,
): VizDatasetEnvelope | null {
  if (!output) return null;
  const datasets: Record<string, VizDataset> = {};

  const dataset = isRenderableDataset(output.datasetForRender)
    ? output.datasetForRender
    : findDataset({
        datasetIndex,
        datasetId: output.datasetId,
        toolCallId,
      });
  if (dataset) {
    datasets[output.datasetId] = dataset;
  }
  if (Object.keys(datasets).length === 0) return null;
  return {
    viz: {
      datasets,
    },
  };
}
